import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ClientLogo {
  id: string;
  name: string;
  logo_url?: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
}

export default function ClientLogosManager() {
  const [logos, setLogos] = useState<ClientLogo[]>([]);
  const [editingLogo, setEditingLogo] = useState<ClientLogo | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchLogos();
  }, []);

  const fetchLogos = async () => {
    const { data, error } = await supabase
      .from('client_logos')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching client logos:', error);
    } else {
      setLogos(data || []);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingLogo) {
      const { error } = await supabase
        .from('client_logos')
        .update(formData)
        .eq('id', editingLogo.id);

      if (error) {
        console.error('Error updating logo:', error);
      } else {
        fetchLogos();
        resetForm();
      }
    } else {
      const { error } = await supabase
        .from('client_logos')
        .insert([formData]);

      if (error) {
        console.error('Error creating logo:', error);
      } else {
        fetchLogos();
        resetForm();
      }
    }
  };

  const handleEdit = (logo: ClientLogo) => {
    setEditingLogo(logo);
    setFormData({
      name: logo.name,
      logo_url: logo.logo_url || '',
      display_order: logo.display_order,
      is_active: logo.is_active
    });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this client logo?')) {
      const { error } = await supabase
        .from('client_logos')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting logo:', error);
      } else {
        fetchLogos();
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      logo_url: '',
      display_order: 0,
      is_active: true
    });
    setEditingLogo(null);
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Client Logos Manager</h1>
        <p className="text-gray-600">Manage client logos displayed on the homepage</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingLogo ? 'Edit Client Logo' : 'Add New Client'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="TechStart Inc"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo URL (Optional)
                </label>
                <input
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com/logo.png"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty to display text name</p>
              </div>

              {formData.logo_url && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo Preview
                  </label>
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <img
                      src={formData.logo_url}
                      alt="Logo preview"
                      className="max-h-16 mx-auto object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '';
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Active
                </label>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 inline-flex justify-center items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingLogo ? 'Update' : 'Create'}
                </button>
                {editingLogo && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Client Logos ({logos.length})</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
              {logos.map((logo) => (
                <div
                  key={logo.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      {logo.logo_url ? (
                        <div className="mb-2">
                          <img
                            src={logo.logo_url}
                            alt={logo.name}
                            className="max-h-12 object-contain"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="mb-2 flex items-center justify-center h-12 bg-gradient-to-br from-blue-50 to-cyan-50 rounded">
                          <span className="text-lg font-bold text-gray-700">{logo.name}</span>
                        </div>
                      )}
                      <h3 className="text-sm font-semibold text-gray-900">{logo.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">Order: {logo.display_order}</span>
                        {!logo.is_active && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 ml-2">
                      <button
                        onClick={() => handleEdit(logo)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(logo.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {logos.length === 0 && (
                <div className="col-span-2 p-12 text-center text-gray-500">
                  No client logos found. Add your first client logo to get started.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
